import { z } from 'zod'
import type { AITool } from './types'
import { formatWeight, formatWeightValue, getWeightUnit } from '../unit-utils'

// OpenAI compatible schema - all fields required (no optional fields)
const parametersSchema = z.object({
  days: z.number()
    .min(7)
    .max(365)
    .describe('Number of days to analyze (7-365). Use 90 for 3 months, 180 for 6 months, etc.')
})


export const getBodyweightTrend: AITool = {
  name: 'getBodyweightTrend',
  description: 'Get bodyweight trends (requires Withings).',
  parameters: parametersSchema,
  
  execute: async (args, context) => {
    const { weightData, userSettings } = context
    const { days } = args
    
    // Get unit system for display
    const unitSystem = userSettings?.settings?.units || 'metric'
    
    // Check if weight data is available
    if (!weightData || weightData.length === 0) {
      return {
        summary: 'No weight data available. Connect your Withings scale in Settings to track bodyweight trends.',
        overallIncrease: null,
        trend: null,
        data: [],
        dataPoints: 0,
        dateRange: null,
        requiresWithingsConnection: true
      }
    }
    
    // Set date range based on requested days
    const now = new Date()
    const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)
    const endDate = now
    
    // Filter weight data within date range
    const filteredData = weightData.filter(point => {
      const pointDate = new Date(point.date)
      return pointDate >= startDate && pointDate <= endDate
    })
    
    if (filteredData.length === 0) {
      return {
        summary: `No weight data found for the specified date range (${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}).`,
        overallIncrease: null,
        trend: null,
        data: [],
        dataPoints: 0,
        dateRange: {
          start: startDate.toISOString().split('T')[0],
          end: endDate.toISOString().split('T')[0]
        }
      }
    }
    
    if (filteredData.length < 2) {
      return {
        summary: `Insufficient weight data for trend analysis (only ${filteredData.length} data point${filteredData.length === 1 ? '' : 's'} found). Need at least 2 measurements.`,
        startWeight: filteredData.length > 0 ? filteredData[0].weight : null,
        endWeight: filteredData.length > 0 ? filteredData[0].weight : null,
        totalChange: 0,
        timespan: `${Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))} days`,
        weeklyRate: 0,
        trendDirection: 'stable',
        dataPoints: filteredData.length,
        currentWeight: filteredData.length > 0 ? formatWeight(filteredData[0].weight, unitSystem) : null,
        dateRange: {
          start: startDate.toISOString().split('T')[0],
          end: endDate.toISOString().split('T')[0]
        }
      }
    }
    
    // Sort by date to ensure proper oldest/newest comparison
    const sortedData = filteredData.sort((a, b) => a.date.getTime() - b.date.getTime())
    
    // Get oldest and newest points for trend analysis
    const oldestPoint = sortedData[0]
    const newestPoint = sortedData[sortedData.length - 1]
    
    // Calculate overall change
    const absoluteChange = newestPoint.weight - oldestPoint.weight // in kg
    const percentageChange = oldestPoint.weight !== 0 
      ? ((newestPoint.weight - oldestPoint.weight) / oldestPoint.weight) * 100
      : 0
    
    // Format the change display with appropriate unit
    const changeDisplay = `${absoluteChange > 0 ? '+' : ''}${formatWeightValue(absoluteChange, unitSystem)}${getWeightUnit(unitSystem)}`
    const percentageDisplay = `${percentageChange > 0 ? '+' : ''}${percentageChange.toFixed(1)}%`
    
    // Calculate time span
    const daySpan = Math.round((newestPoint.date.getTime() - oldestPoint.date.getTime()) / (1000 * 60 * 60 * 24))
    
    // Generate summary based on the change
    let summary: string
    const oldestWeight = formatWeight(oldestPoint.weight, unitSystem)
    const newestWeight = formatWeight(newestPoint.weight, unitSystem)
    
    if (Math.abs(percentageChange) < 1) {
      summary = `Weight remained stable over ${daySpan} days (${percentageDisplay}, ${changeDisplay}), from ${oldestWeight} to ${newestWeight}.`
    } else if (absoluteChange > 0) {
      summary = `Weight increased by ${percentageDisplay} (${changeDisplay}) over ${daySpan} days, from ${oldestWeight} to ${newestWeight}.`
    } else {
      summary = `Weight decreased by ${Math.abs(percentageChange).toFixed(1)}% (${changeDisplay}) over ${daySpan} days, from ${oldestWeight} to ${newestWeight}.`
    }
    
    // Calculate weekly rate (change per week)
    const weeklyRate = daySpan > 0 ? (absoluteChange / daySpan) * 7 : 0
    
    return {
      summary,
      startWeight: oldestPoint.weight,
      endWeight: newestPoint.weight,
      totalChange: absoluteChange,
      timespan: `${daySpan} days`,
      weeklyRate: Math.round(weeklyRate * 100) / 100, // Round to 2 decimal places
      trendDirection: Math.abs(percentageChange) < 1 ? 'stable' : 
                     absoluteChange > 0 ? 'increasing' : 'decreasing',
      dataPoints: sortedData.length,
      currentWeight: newestWeight,
      dateRange: {
        start: startDate.toISOString().split('T')[0],
        end: endDate.toISOString().split('T')[0]
      }
    }
  }
}